import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

import { dataUser, spocLocationDetailsZoho } from '../Helpers/util';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwInput from '../Common/FormComponent/SwInput';
import dayjs from 'dayjs';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import AntDTimePicker from '../Common/AntDDateTimePicker/AntDTimePicker';

const MeetingRoomServiceRequest = forwardRef(({ onClose }, ref) => {
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [locationData, setLocationData] = useState([]);
    const [meetingRoomNo, setMeetingRoomNo] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const [locationError, setLocationError] = useState('');
    const [meetingRoomError, setMeetingRoomError] = useState('');
    const [startDateError, setStartDateError] = useState('');
    const [endDateError, setEndDateError] = useState('');
    const [startTimeError, setStartTimeError] = useState('');
    const [endTimeError, setEndTimeError] = useState('');

    const [loading, setLoading] = useState(false);

    const UserObj = JSON.parse(dataUser());
    const locationZoho = spocLocationDetailsZoho();

    useEffect(() => {
        setLocationData(JSON.parse(locationZoho));
    }, []);

    // expose methods to SwOffCanvas
    useImperativeHandle(ref, () => ({
        resetForm() {
            resetValue();
        },
        submitRequest() {
            handleSubmit();
        },
        loading,
    }));

    const handleSubmit = async () => {
        if (!selectedLocation) {
            setLocationError("Location can't be empty");
            return;
        }
        if (!meetingRoomNo) {
            setMeetingRoomError("No. of meeting room can't be empty");
            return;
        }
        if (!startDate) {
            setStartDateError("Start date can't be empty");
            return;
        }
        if (!endDate) {
            setEndDateError("End date can't be empty");
            return;
        }
        if (!startTime) {
            setStartTimeError("Start Time can't be empty");
            return;
        }

        if (!endTime) {
            setEndTimeError("End Time can't be empty");
            return;
        }


        setLoading(true);
        const authToken = JSON.parse(localStorage.getItem('headData'));

        const param = {
            product_name: 'Meeting Room',
            start_date: startDate,
            end_date: endDate,
            start_time: startTime.format('HH:mm'),
            end_time: endTime.format('HH:mm'),
            noOfDesk: meetingRoomNo,
            company_name: UserObj.company_name,
            email: UserObj.email,
            fname: UserObj.fname,
            lname: UserObj.lname,
            phone: UserObj.phone,
            location_name: selectedLocation.label,
            location_id: selectedLocation.value,
        };

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}basic-data/new-product-enquiry`,
                param,
                {
                    headers: {
                        Authorization: authToken?.spintlyToken,
                    },
                },
            );

            if (res.status === 200) {
                toast.success(res.data.message);
                onClose(true);
            }
        } catch (err) {
            toast.error('Unable to submit Meeting Room request');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const resetValue = () => {
        setSelectedLocation(null);
        setMeetingRoomNo('');
        setStartDate('');
        setEndDate('');
        ;
        setLocationError('');
        setMeetingRoomError('');
        setStartDateError('');
        setEndDateError('');

        setStartTime(null);
        setEndTime(null);
        setStartTimeError('');
        setEndTimeError('');

    };

    return (
        <>
            {/* Location */}
            <div className="SwFormGroup">
                <label className="SwFormLabel">
                    Location <span className="Required">*</span>
                </label>
                <SwSelect
                    value={selectedLocation}
                    onChange={(opt) => {
                        setSelectedLocation(opt);
                        setLocationError('');
                    }}
                    options={locationData}
                />
                <span className="SwTextDanger SwTextSmall"> {locationError}</span>
            </div>

            {/* No. of Meeting Room */}
            <div className="SwFormGroup">
                <label className="SwFormLabel">
                    No. of Meeting Room <span className="Required">*</span>
                </label>
                <SwInput
                    type="number"
                    min="0"
                    value={meetingRoomNo}
                    onChange={(e) => {
                        setMeetingRoomNo(e.target.value);
                        setMeetingRoomError('');
                    }}
                />
                <span className="SwTextDanger SwTextSmall">{meetingRoomError}</span>
            </div>

            {/* Dates */}
            <div className="row">
                <div className="col-md-6 pr-2">
                    <div className="SwFormGroup">
                        <label className="SwFormLabel">
                            Start Date <span className="Required">*</span>
                        </label>
                        <AntDDatePicker
                            value={startDate ? dayjs(startDate) : null}
                            onChange={(v) => {
                                setStartDate(v ? v.format('YYYY-MM-DD') : '');
                                setStartDateError('');
                            }}
                            placeholder="Start Date"
                            disabledDate={(c) => c && c < dayjs().startOf('day')}
                        />
                        <span className="SwTextDanger SwTextSmall">{startDateError}</span>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="SwFormGroup">
                        <label className="SwFormLabel">
                            End Date <span className="Required">*</span>
                        </label>
                        <AntDDatePicker
                            value={endDate ? dayjs(endDate) : null}
                            onChange={(v) => {
                                setEndDate(v ? v.format('YYYY-MM-DD') : '');
                                setEndDateError('');
                            }}
                            placeholder="End Date"
                            disabledDate={(c) =>
                                c && c < dayjs(startDate || undefined).startOf('day')
                            }
                        />
                        <span className="SwTextDanger SwTextSmall">{endDateError}</span>
                    </div>
                </div>
            </div>

            {/* Time */}
            <div className="row">
                <div className="col-md-6 pr-2">
                    <div className="SwFormGroup">
                        <label className="SwFormLabel">
                            Start Time <span className="Required">*</span>
                        </label>
                        <AntDTimePicker
                            value={startTime}
                            format="HH:mm"
                            placeholder="Start Time"
                            onChange={(time) => {
                                setStartTime(time);   // 👈 store dayjs
                                setStartTimeError('');
                            }}
                        />


                        <span className="SwTextDanger SwTextSmall">{startTimeError}</span>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="SwFormGroup">
                        <label className="SwFormLabel">
                            End Time <span className="Required">*</span>
                        </label>
                        <AntDTimePicker
                            value={endTime}
                            format="HH:mm"
                            placeholder="End Time"
                            onChange={(time) => {
                                setEndTime(time);     // 👈 store dayjs
                                setEndTimeError('');
                            }}
                        />


                        <span className="SwTextDanger SwTextSmall">{endTimeError}</span>
                    </div>
                </div>
            </div>
        </>
    );
});

export default MeetingRoomServiceRequest;
